import { Spacing } from '@persona-id/design-tokens';
import { PersonaIcon } from '@persona-id/design-tokens/icons';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import ResultObject from 'dashboard/components/WorkflowControlPanel/ResultObject';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import ChangeTrigger from 'dashboard/components/WorkflowControlPanel/TriggerConfigEvent/ChangeTrigger';
import ParamsTriggerEvent from 'dashboard/components/WorkflowControlPanel/TriggerConfigEvent/ParamsTriggerEvent';
import { LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { TransactionType } from 'dashboard/models/transaction-type';
import { SupportedTriggerEvent, TriggerEvent } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { TriggerObject } from 'dashboard/models/workflow-run';
import { WorkflowRunStepKnownObjects } from 'dashboard/models/workflow-step';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { headingMixins } from 'design-system/mixins/text';
import { EVENT_NAME_TO_EVENT_TYPE, EventName, EventType } from 'lib/constants/events';
import useWorkflowTriggerableEvents from 'lib/hooks/useWorkflowTriggerableEvents';
import { ApiModel } from 'lib/interfaces/api';
import { TokensToNode } from 'lib/workflows';

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  justify-content: space-between;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubTitle = styled.div`
  ${headingMixins.sm}
  padding-bottom: ${Spacing[2]};
`;

interface TriggerConfigEventProps {
  trigger: TriggerEvent;
  editable: boolean;
  errors: WorkflowError[];
  workflowSchema: WorkflowSchema;
  transactionTypes: TransactionType[];
  tokensToSteps: TokensToNode;
  triggerObject: TriggerObject | null;
  handleClose: () => void;
  updateTrigger: (newTrigger: TriggerEvent, template?: TemplateFilter) => void;
  workflowVersionExpiration: number;
  updateWorkflowVersionExpiration: (expiration: number) => void;
}

const TriggerConfigEvent: FC<TriggerConfigEventProps> = ({
  trigger,
  editable,
  errors,
  workflowSchema,
  transactionTypes,
  tokensToSteps,
  handleClose,
  updateTrigger,
  triggerObject,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
}) => {
  const currentEventType = EVENT_NAME_TO_EVENT_TYPE[trigger.attributes['event-name']];
  const triggerableEvents = useWorkflowTriggerableEvents();
  const filteredEvents = triggerableEvents.filter(
    (value) => EVENT_NAME_TO_EVENT_TYPE[value] === currentEventType,
  );

  const [isEditingTriggerObjectType, setIsEditingTriggerObjectType] = useState(false);

  const updateTransactionTemplateFilter = (filter: string) => {
    updateTrigger(
      {
        ...trigger,
        attributes: { ...trigger.attributes, 'template-filters': { 'transaction-type': [filter] } },
      },
      { type: ApiModel.Transaction, id: filter },
    );
  };

  const updateTriggerRules = (rules: LogicBlockOrV2) => {
    updateTrigger({
      ...trigger,
      attributes: { ...trigger.attributes, rules },
    });
  };

  const updateTriggerEventName = useCallback(
    (newName: SupportedTriggerEvent) => {
      updateTrigger({
        ...trigger,
        attributes: {
          ...trigger.attributes,
          ...(EVENT_NAME_TO_EVENT_TYPE[newName] !== currentEventType // reset rules and template-filters if object type is changed
            ? {
                rules: true,
                'template-filters': {},
              }
            : {}),
          'event-name': newName,
        },
      });
    },
    [currentEventType, updateTrigger, trigger],
  );

  const handleSelectTriggerObjectType = useCallback(
    (chosenEventType: EventType) => {
      // if event type changed, choose a default event for the trigger
      if (currentEventType !== chosenEventType) {
        switch (chosenEventType) {
          case EventType.Account:
            updateTriggerEventName(EventName.AccountCreated);
            break;
          case EventType.Case:
            updateTriggerEventName(EventName.CaseCreated);
            break;
          case EventType.CaseMessage:
            updateTriggerEventName(EventName.CaseMessageReceived);
            break;
          case EventType.Report:
            updateTriggerEventName(EventName.ReportAdverseMediaMatched);
            break;
          case EventType.Inquiry:
            updateTriggerEventName(EventName.InquiryCreated);
            break;
          case EventType.SAR:
            updateTriggerEventName(EventName.SARCreated);
            break;
          case EventType.Transaction:
            updateTriggerEventName(EventName.TransactionCreated);
            break;
        }
      }
      setIsEditingTriggerObjectType(false);
    },
    [currentEventType, updateTriggerEventName],
  );

  return (
    <>
      {isEditingTriggerObjectType ? (
        <ChangeTrigger
          initialEventType={EVENT_NAME_TO_EVENT_TYPE[trigger.attributes['event-name']]}
          handleSelectTriggerObjectType={handleSelectTriggerObjectType}
        />
      ) : (
        <Container>
          <HorizontalContainer>
            <SubTitle>Trigger Step</SubTitle>
            {editable && (
              <Button
                id='trigger-config-event__change-object-type'
                variant='basic'
                width='auto'
                aria-label='Change trigger object type'
                onClick={() => setIsEditingTriggerObjectType(true)}
              >
                Change
              </Button>
            )}
          </HorizontalContainer>
          <Spacer height={2} />
          <StepConfigTypeCard
            icon={<PersonaIcon size={24} />}
            title={`${currentEventType} Event`}
            description={`Trigger based on the ${currentEventType} event`}
          />
          <Spacer height={2} />
          <SubTitle>Configuration</SubTitle>
          <ParamsTriggerEvent
            editable={editable}
            errors={errors}
            workflowSchema={workflowSchema}
            events={filteredEvents}
            updateTransactionTemplateFilter={updateTransactionTemplateFilter}
            updateTriggerRules={updateTriggerRules}
            updateTriggerEventName={updateTriggerEventName}
            trigger={trigger}
            tokensToSteps={tokensToSteps}
            transactionTypes={transactionTypes}
            workflowVersionExpiration={workflowVersionExpiration}
            updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
          />
        </Container>
      )}
      {triggerObject && (
        <>
          <Spacer height={3} />
          <ResultObject object={triggerObject as WorkflowRunStepKnownObjects} />
        </>
      )}
      <Spacer height={3} />
      <HStack equal>
        {editable && (
          <Button disabled aria-label='Delete'>
            Delete
          </Button>
        )}
        <Button id='trigger-config-event__close' aria-label='Close' onClick={handleClose}>
          Close
        </Button>
      </HStack>
    </>
  );
};

export default TriggerConfigEvent;
